import { Mark, mergeAttributes, type MarkViewProps } from "@tiptap/core";
import { MarkViewContent, ReactMarkViewRenderer } from "@tiptap/react";
import { useRef, type JSX, type MouseEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { LinkPreview, UrlLinkPreview } from "@/app/elements/link-preview";
import { repositories } from "@/store/repositories";
import { uiDispatch } from "@/store/ui.store";

function ResearchSourceLink({
    href,
    attributes,
    children,
}: {
    href: string;
    attributes: Record<string, unknown>;
    children: ReactNode;
}): JSX.Element {
    const navigate = useNavigate();
    const inFlight = useRef(false);
    async function open(event: MouseEvent<HTMLAnchorElement>): Promise<void> {
        event.preventDefault();
        event.stopPropagation();
        if (inFlight.current) return;
        inFlight.current = true;
        try {
            const id = decodeURIComponent(href.split("?")[0]!.slice("/note/".length));
            const source = await repositories.notes.getOne(id);
            if (!source || source.deletedAt)
                throw new Error(
                    "The source note is unavailable. Restore it or open its workspace, then try Open source again.",
                );
            navigate(href);
        } catch (reason) {
            uiDispatch.setAlert({
                open: true,
                type: "error",
                title: "Source unavailable",
                message: reason instanceof Error ? reason.message : "Could not open the source. Try Open source again.",
            });
        } finally {
            inFlight.current = false;
        }
    }
    return (
        <a {...attributes} href={href} target="_self" onClick={(event) => void open(event)}>
            {children}
        </a>
    );
}

type LinkMarkOptions = {
    HTMLAttributes: Record<string, string>;
};

const getStringAttribute = (value: unknown): string => (typeof value === "string" ? value : "");

const hasCustomLabel = (element: HTMLElement, href: string): boolean => {
    const label = element.textContent?.trim() ?? "";
    return Boolean(label && label !== href.trim());
};

function LinkMarkView({ mark, HTMLAttributes }: MarkViewProps) {
    const href = getStringAttribute(mark.attrs.href);
    const title = getStringAttribute(mark.attrs.title) || href;
    if (!href) return <MarkViewContent />;
    const linkAttributes = mergeAttributes(HTMLAttributes, {
        href,
        title,
        "data-link-url": href,
    });

    if (/^\/note\/[^/?]+\?/.test(href) && new URLSearchParams(href.split("?")[1]).get("research") === "1") {
        return (
            <ResearchSourceLink href={href} attributes={linkAttributes}>
                <MarkViewContent />
            </ResearchSourceLink>
        );
    }

    if (/^\.\/comments_[^#]+\.md#[^#]+$/.test(href)) {
        return (
            <a {...linkAttributes} target="_self" data-note-comment="true">
                <MarkViewContent />
            </a>
        );
    }

    return (
        <LinkPreview
            trigger={
                <a {...linkAttributes}>
                    <MarkViewContent />
                </a>
            }
        >
            <UrlLinkPreview title={title} url={href} />
        </LinkPreview>
    );
}

export const LinkMark = Mark.create<LinkMarkOptions>({
    name: "link",
    priority: 40,
    inclusive: false,
    addOptions() {
        return {
            HTMLAttributes: {
                class: "link",
                target: "_blank",
                rel: "noopener noreferrer",
            },
        };
    },
    addAttributes() {
        return {
            href: {
                default: null,
                parseHTML: (element) => element.getAttribute("href"),
                renderHTML: (attributes) => {
                    if (!attributes.href) return {};
                    return { href: attributes.href };
                },
            },
            title: {
                default: null,
                parseHTML: (element) => element.getAttribute("title"),
                renderHTML: (attributes) => {
                    if (!attributes.title) return {};
                    return { title: attributes.title };
                },
            },
        };
    },
    parseHTML() {
        return [
            {
                tag: "a[href]",
                priority: 40,
                getAttrs: (element) => {
                    if (!(element instanceof HTMLElement)) return false;
                    if (element.getAttribute("data-type") === "mention") return false;
                    const href = element.getAttribute("href");
                    if (!href || !hasCustomLabel(element, href)) return false;
                    return null;
                },
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        const href = getStringAttribute(HTMLAttributes.href);
        const title = getStringAttribute(HTMLAttributes.title) || href;
        const hoverAttributes = href ? { title, "data-link-url": href } : {};
        return ["a", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, hoverAttributes), 0];
    },
    addMarkView() {
        return ReactMarkViewRenderer(LinkMarkView);
    },
});
